import type { DisciplineSlug } from "@jumpbean/shared";

export interface ChallengeLoadingStateProps {
  /** Discipline for theming */
  discipline?: DisciplineSlug;
  /** Optional message to display */
  message?: string;
  /** Character name */
  characterName?: string;
}

/**
 * Loading state component shown between challenges.
 * Features a playful animation that matches the discipline theme.
 */
export function ChallengeLoadingState({
  discipline = "math",
  message = "Loading your next challenge...",
  characterName,
}: ChallengeLoadingStateProps) {
  return (
    <div
      className={`
        flex flex-col items-center justify-center
        min-h-screen p-8
        ${getDisciplineBackground(discipline)}
      `}
    >
      {/* Animated Character */}
      <div className="relative mb-8">
        <div
          className={`
            w-32 h-32 rounded-full
            flex items-center justify-center
            animate-float
            ${getDisciplineGradient(discipline)}
            shadow-lg
          `}
        >
          <span className="text-6xl">{getCharacterEmoji(discipline)}</span>
        </div>

        {/* Sparkles around character */}
        <div className="absolute -top-2 -right-2">
          <Sparkle delay={0} />
        </div>
        <div className="absolute -bottom-1 -left-3">
          <Sparkle delay={0.3} />
        </div>
        <div className="absolute top-1/2 -right-4">
          <Sparkle delay={0.6} />
        </div>
      </div>

      {/* Loading message */}
      <div className="text-center">
        {characterName && (
          <h2 className="font-display font-bold text-display-sm text-neutral-800 mb-2">
            {characterName}
          </h2>
        )}
        <p className="font-body text-body-lg text-neutral-600 animate-pulse">
          {message}
        </p>
      </div>

      {/* Loading dots */}
      <div className="flex gap-2 mt-6">
        <LoadingDot delay={0} discipline={discipline} />
        <LoadingDot delay={0.2} discipline={discipline} />
        <LoadingDot delay={0.4} discipline={discipline} />
      </div>
    </div>
  );
}

/**
 * Animated loading dot
 */
function LoadingDot({
  delay,
  discipline,
}: {
  delay: number;
  discipline: DisciplineSlug;
}) {
  return (
    <div
      className={`
        w-4 h-4 rounded-full
        animate-bounce
        ${getDisciplineDotColor(discipline)}
      `}
      style={{ animationDelay: `${delay}s` }}
    />
  );
}

/**
 * Animated sparkle element
 */
function Sparkle({ delay }: { delay: number }) {
  return (
    <div
      className="animate-sparkle"
      style={{ animationDelay: `${delay}s` }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        className="text-sunshine-500"
      >
        <path
          d="M12 2L13.09 8.26L19 9L13.09 9.74L12 16L10.91 9.74L5 9L10.91 8.26L12 2Z"
          fill="currentColor"
        />
      </svg>
    </div>
  );
}

/**
 * Get background style for discipline
 */
function getDisciplineBackground(discipline: DisciplineSlug): string {
  const backgrounds: Record<DisciplineSlug, string> = {
    math: "bg-gradient-to-b from-luna-light/20 to-luna-light/40",
    spelling: "bg-gradient-to-b from-petal-light/20 to-petal-light/40",
    patterns: "bg-gradient-to-b from-prism-yellow/20 to-prism-blue/40",
    typing: "bg-gradient-to-b from-whiskers-light/20 to-whiskers-light/40",
    programming: "bg-gradient-to-b from-pixel-light/20 to-pixel-light/40",
  };
  return backgrounds[discipline];
}

/**
 * Get gradient for discipline
 */
function getDisciplineGradient(discipline: DisciplineSlug): string {
  const gradients: Record<DisciplineSlug, string> = {
    math: "bg-gradient-to-br from-luna-light to-luna-dark",
    spelling: "bg-gradient-to-br from-petal-light to-petal-dark",
    patterns: "bg-gradient-to-br from-prism-red via-prism-yellow to-prism-blue",
    typing: "bg-gradient-to-br from-whiskers-light to-whiskers-dark",
    programming: "bg-gradient-to-br from-pixel-light to-pixel-dark",
  };
  return gradients[discipline];
}

/**
 * Get character emoji for discipline
 */
function getCharacterEmoji(discipline: DisciplineSlug): string {
  const emojis: Record<DisciplineSlug, string> = {
    math: "🦄",
    spelling: "🧚",
    patterns: "🐉",
    typing: "🐱",
    programming: "🦉",
  };
  return emojis[discipline];
}

/**
 * Get dot color for discipline
 */
function getDisciplineDotColor(discipline: DisciplineSlug): string {
  const colors: Record<DisciplineSlug, string> = {
    math: "bg-luna",
    spelling: "bg-petal",
    patterns: "bg-prism-red",
    typing: "bg-whiskers",
    programming: "bg-pixel",
  };
  return colors[discipline];
}

/**
 * Transition overlay between challenges
 */
export function ChallengeTransition({
  discipline = "math",
  isCorrect,
  onComplete,
}: {
  discipline?: DisciplineSlug;
  isCorrect: boolean;
  onComplete: () => void;
}) {
  return (
    <div
      className={`
        fixed inset-0 z-50
        flex items-center justify-center
        ${isCorrect ? "bg-meadow-400/90" : "bg-neutral-200/90"}
        animate-fade-in
      `}
      onClick={onComplete}
    >
      <div className="text-center">
        {isCorrect ? (
          <>
            <div className="text-8xl mb-4 animate-celebrate">⭐</div>
            <h2 className="font-display font-bold text-display-lg text-white">
              Great Job!
            </h2>
          </>
        ) : (
          <>
            <div className="text-6xl mb-4">{getCharacterEmoji(discipline)}</div>
            <h2 className="font-display font-bold text-display-md text-neutral-700">
              Try Again!
            </h2>
            <p className="font-body text-body-lg text-neutral-600 mt-2">
              You can do it!
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default ChallengeLoadingState;
